/**
 * 213 (#309): the shape `backend/src/modules/bookings/conflictAlternatives.ts`'s `AlternativeCar`
 * serializes as, read off `ApiError.alternatives` (an untyped `unknown[]` — the backend's own
 * `details` payload has no fixed shape across every future use of it).
 */
export interface AlternativeCar {
  id: string;
  name: string;
  pricing: {
    per_hour_minor: number | null;
    per_day_minor: number | null;
    currency_symbol: string;
  } | null;
}

function formatAlternativePrice(pricing: AlternativeCar['pricing']): string | null {
  if (!pricing) return null;
  const minor = pricing.per_hour_minor ?? pricing.per_day_minor;
  if (minor == null) return null;
  const unit = pricing.per_hour_minor != null ? '/hr' : '/day';
  return `${pricing.currency_symbol}${(minor / 100).toFixed(2)}${unit}`;
}

interface AlternativeCarsProps {
  items: AlternativeCar[];
  /** 213: change-car's list is clickable directly into the existing plate-search picker — the
   * booking's window/location never change here, only which car fills them. Omit for reschedule,
   * where there is no "pick this one instead" action, only the list itself. */
  onSelect?: (car: AlternativeCar) => void;
}

/** A refusal names other cars free for the same window — a shortlist, not a second search page
 * (the backend's own cap is already applied). */
export function AlternativeCars({ items, onSelect }: AlternativeCarsProps) {
  if (items.length === 0) return null;
  return (
    <div className="mt-2" data-testid="alternative-cars">
      <p className="text-sm text-steel mb-1">Other cars free for this window:</p>
      <ul className="grid gap-1">
        {items.map((car) => (
          <li key={car.id}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(car)}
                className="w-full text-left text-sm px-3 py-2 rounded border border-hairline hover:bg-surface-alt flex items-center justify-between gap-3"
              >
                <span>{car.name}</span>
                {formatAlternativePrice(car.pricing) && (
                  <span className="text-steel">{formatAlternativePrice(car.pricing)}</span>
                )}
              </button>
            ) : (
              <div className="text-sm px-3 py-2 rounded border border-hairline flex items-center justify-between gap-3">
                <span>{car.name}</span>
                {formatAlternativePrice(car.pricing) && (
                  <span className="text-steel">{formatAlternativePrice(car.pricing)}</span>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
